import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { emailValidation } from './validation';
import {
  Alert,
  Box,
  Button,
  Typography,
  Link
} from '@mui/material';
import { InputText } from '../../elements/InputText';
import InputPassword from '../../elements/InputPassword';
import { useResponseError } from '../../hooks/useResponseError';
import { STORAGE_AUTH_TOKEN, STORAGE_USER, STORAGE_USER_CONFIRM_EMAIL } from '../../constants/storage';
import { loginAuth } from '../../services/authService';
import { useAuth } from '../../hooks/useAuth';
import { getUserData } from '../../services/userService';
import { ErrorBox } from '../../elements/ErrorBox';
import { sendGtmEvent } from '../../utils';

export const LoginForm = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { responseError, setResponseError } = useResponseError();
  const [needsVerify, setNeedsVerify] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: any): Promise<any> => {
    setNeedsVerify(false);
    try {
      sendGtmEvent('login_attempt', {
        email: data?.email,
      });
      const res = await loginAuth(data);
      login(res.accessToken);
      const resUserData = await getUserData({
        token: res?.accessToken
      });
      localStorage.setItem(STORAGE_AUTH_TOKEN, res?.accessToken);

      if (resUserData) {
        localStorage.setItem(STORAGE_USER, JSON.stringify(resUserData));
        navigate(`/dashboard`, {
          replace: true
        });
        window.location.reload();
      }
    } catch (error: unknown) {
      const message = (error as Error).message || '';
      sendGtmEvent('login_error', {
        email: data?.email,
        error: message
      });
      if (message.toLowerCase().includes('verify')) {
        setNeedsVerify(true);
        localStorage.setItem(STORAGE_USER_CONFIRM_EMAIL, data.email);
      }
      setResponseError(message);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: '315px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Box
        sx={{
          width: '100%'
        }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Typography
          align="center"
          variant="h1"
          sx={{
            mb: '8px'
          }}
        >
          Sign in
        </Typography>
        <Typography
          align="center"
          variant="body1"
          sx={{
            mb: '24px',
            fontSize: '14px',
            lineHeight: '20px',
            color: '#455445',
          }}
        >
          Book and manage your Lucky Services cleanings.
        </Typography>
        <Box>
          <InputText
            inputProps={{
              ...register('email'),
              autoComplete: 'username',
              inputMode: 'email',
              autoCapitalize: 'none',
              spellCheck: false,
            }}
            type='email'
            label='Email address'
            placeholder='you@email.com'
            error={!!errors['email']}
            helperText={errors['email'] ? errors['email'].message : ''}
            fullWidth
          />
        </Box>
        <Box>
          <InputPassword
            inputProps={{
              ...register('password'),
              autoComplete: 'current-password',
            }}
            placeholder='Enter your password'
            error={!!errors['password']}
            fullWidth
          />
          {errors['password'] && <ErrorBox
            sx={{
              mb: '8px',
              textAlign: 'left',
            }}>
            {errors['password'].message || 'Something went wrong'}
          </ErrorBox>
          }
        </Box>
        <Box sx={{
          mb: '20px',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <Link
            href="/request-reset"
            sx={{ textTransform: 'none', fontSize: '13px', letterSpacing: 'normal' }}>
            Forgot password?
          </Link>
        </Box>
        {responseError && <Alert
          severity="error"
          sx={{
            mb: '16px',
            alignItems: 'center',
            '& .MuiAlert-message': { fontSize: '14px', lineHeight: '20px' },
          }}>
          {responseError || 'Something went wrong'}
          {needsVerify && (
            <Box sx={{ mt: '10px' }}>
              <Link href="/verify">Resend verification email</Link>
            </Box>
          )}
        </Alert>
        }
        <Button
          fullWidth
          variant="contained"
          type="submit"
          disabled={isSubmitting}
          sx={{
            py: '12px',
            fontSize: '18px',
            lineHeight: '24px',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '12px',
            backgroundColor: '#316E59',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#28584A',
            },
            '&.Mui-disabled': {
              backgroundColor: '#316E59',
              color: '#FFFFFF',
              opacity: 0.6,
            },
          }}
        >
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </Box>
    </Box>
  );
};

const schema = yup.object().shape({
  email: emailValidation,
  password: yup.string().required('Password is required'),
});
